import { useCallback } from 'react'

import { useBrands, useCreateBrand, type ProductBrand } from '@/app/entities/brands/model'

import { useUpdateProductBrand } from './useUpdateProductBrand'

export const normalizeBrandName = (name: string) => name.trim().toLowerCase()

export const areBrandNamesEqual = (first: string | null | undefined, second: string | null | undefined): boolean => {
    const normalizedFirst = first ? normalizeBrandName(first) : ''
    const normalizedSecond = second ? normalizeBrandName(second) : ''

    if (!normalizedFirst || !normalizedSecond) {
        return false
    }

    return normalizedFirst === normalizedSecond
}

export const findBrandByName = (brands: ProductBrand[], name: string): ProductBrand | undefined => {
    const normalized = normalizeBrandName(name)
    if (!normalized) return undefined
    return brands.find(brand => areBrandNamesEqual(brand.name, normalized))
}

export const useApplySuggestedBrand = (suggestedBrandName: string) => {
    const trimmedName = suggestedBrandName.trim()
    const { data: brandsPage } = useBrands()
    const { mutate: createBrand, isPending: isCreating, error: createError } = useCreateBrand()
    const { mutate: updateProductBrand, isPending: isUpdating, error: updateError } = useUpdateProductBrand()

    const apply = useCallback(
        (onSuccess?: () => void) => {
            if (!trimmedName || isCreating || isUpdating) return

            const brands = brandsPage?.items ?? []
            const existingBrand = findBrandByName(brands, trimmedName)

            if (existingBrand) {
                updateProductBrand(existingBrand.id, { onSuccess })
                return
            }

            createBrand(trimmedName, {
                onSuccess: brand => {
                    updateProductBrand(brand.id, { onSuccess })
                },
            })
        },
        [brandsPage?.items, createBrand, isCreating, isUpdating, trimmedName, updateProductBrand]
    )

    return {
        apply,
        isPending: isCreating || isUpdating,
        error: createError ?? updateError,
    }
}
